import { useCallback, useEffect, useRef, useState } from 'react';
import { TimezoneSelect, browserTimezone } from '../TimezoneSelect';
import { api, ApiError } from '../../lib/api';
import { fromMeters, hhmm, hoursOf, shortDate, toMeters } from '../../lib/format';
import { useAuth } from '../AuthGate';
import { useUi } from '@allrouted/ui/Toaster';
import {
  Disclosure,
  Field,
  useEditing,
  NotCaptured,
  NumberInput,
  SettingsCard,
  TextArea,
  TextInput,
  TimeInput,
  Toggle,
} from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { TagsInput } from '@allrouted/ui/TagsInput';
import { StopSlider } from '@allrouted/ui/StopSlider';
import {
  BellIcon,
  CalendarPlusIcon,
  CopyIcon,
  CopyIntoIcon,
  IconButton,
  PlusIcon,
  TrashIcon,
} from '@allrouted/ui/Icons';
import { Drawer } from '@allrouted/ui/Drawer';
import { DatePicker } from '@allrouted/ui/DatePicker';
import { SetupNav, type SetupPage } from './SetupNav';
import { TagsPage } from './TagsPage';
import { RecurrencesPage } from './RecurrencesPage';
import { WebhooksPage } from './WebhooksPage';
import { NotificationsPage } from './NotificationsPage';
import { AssignmentPage } from './AssignmentPage';
import { DriverSignIn } from './DriverSignIn';
import { DriverNotifyCard, SendNotificationDrawer } from './DriverNotifications';

/**
 * Territory configuration.
 *
 * One page per screen in the incumbent tree. Pages backed by something we
 * actually observed are fully wired; the rest carry a NotCaptured banner so a
 * plausible form is never mistaken for a verified one.
 */
export function SetupView({
  territoryId,
  page,
  onSelectPage,
  onSelectTerritory,
  onTerritoriesChanged,
}: {
  territoryId: string | null;
  /* Which page is open lives in the URL, so a settings screen can be linked. */
  page: SetupPage;
  onSelectPage: (page: SetupPage) => void;
  /* The territories page can switch which one everything else is scoped to. */
  onSelectTerritory: (territoryId: string) => void;
  /* And tell the header when the list itself changed. */
  onTerritoriesChanged: () => void;
}) {
  return (
    <div className="setup">
      <SetupNav page={page} onSelect={onSelectPage} />
      <div className="setup-body">
        {page === 'territory' ? (
          /* The one page that works without a territory: it is where one is made. */
          <TerritoryPage
            territoryId={territoryId}
            onSelectTerritory={onSelectTerritory}
            onTerritoriesChanged={onTerritoriesChanged}
          />
        ) : territoryId ? (
          <SetupPageBody
            page={page}
            territoryId={territoryId}
            onSelectTerritory={onSelectTerritory}
            onTerritoriesChanged={onTerritoriesChanged}
          />
        ) : (
          <div className="setup-empty">
            <h2>Create a territory first</h2>
            <p>Depots, vehicles, drivers and everything else on these pages belong to a territory.</p>
            <button type="button" className="btn btn-primary" onClick={() => onSelectPage('territory')}>
              Go to Territories
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SetupPageBody({
  page,
  territoryId,
  onSelectTerritory,
  onTerritoriesChanged,
}: {
  page: SetupPage;
  territoryId: string;
  onSelectTerritory: (territoryId: string) => void;
  onTerritoriesChanged: () => void;
}) {
  switch (page) {
    case 'depots':
      return <DepotsPage territoryId={territoryId} />;
    case 'vehicles':
      return <VehiclesPage territoryId={territoryId} />;
    case 'drivers':
      return <DriversPage territoryId={territoryId} />;
    case 'assignment':
      return <AssignmentPage territoryId={territoryId} />;
    case 'tags':
      return <TagsPage territoryId={territoryId} />;
    case 'regions':
      return <RegionsPage territoryId={territoryId} />;
    case 'road-editor':
      return <RoadEditorPage territoryId={territoryId} />;
    case 'traffic':
      return <TrafficPage territoryId={territoryId} />;
    case 'tracking-page':
      return <TrackingPagePage territoryId={territoryId} />;
    case 'notifications':
      return <NotificationsPage territoryId={territoryId} />;
    case 'preferences':
      return <PreferencesPage territoryId={territoryId} />;
    case 'voice':
      return <VoicePage territoryId={territoryId} />;
    case 'mobile-app':
      return <MobileAppPage territoryId={territoryId} />;
    case 'recurrences':
      return <RecurrencesPage territoryId={territoryId} />;
    case 'webhooks':
      return <WebhooksPage territoryId={territoryId} />;
    case 'territory':
      return (
        <TerritoryPage
          territoryId={territoryId}
          onSelectTerritory={onSelectTerritory}
          onTerritoriesChanged={onTerritoriesChanged}
        />
      );
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ *
 * a small hook for load-edit-save pages
 * ------------------------------------------------------------------ */

function useRecord<T extends Record<string, unknown>>(
  load: () => Promise<T>,
  save: (patch: Partial<T>) => Promise<unknown>,
  label: string,
) {
  const ui = useUi();
  const [value, setValue] = useState<T | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      setValue(await load());
      setDirty(false);
    } catch (err) {
      ui.notify('error', `Could not load ${label}`, err instanceof ApiError ? err.message : String(err));
    }
    // load identity changes every render for inline closures; the caller passes
    // a stable territoryId so re-running on mount is what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const set = <K extends keyof T>(key: K, next: T[K]) => {
    setValue((v) => (v ? { ...v, [key]: next } : v));
    setDirty(true);
  };

  const commit = async () => {
    if (!value) return;
    setSaving(true);
    try {
      await save(value);
      ui.notify('ok', `${label} saved`);
      setDirty(false);
    } catch (err) {
      ui.notify('error', `Could not save ${label}`, err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return { value, set, commit, dirty, saving, reload };
}

/* ------------------------------------------------------------------ *
 * Depots
 * ------------------------------------------------------------------ */

interface Address {
  line1?: string | null;
  city?: string | null;
  region?: string | null;
  postcode?: string | null;
  country?: string | null;
}

interface Depot {
  id: string;
  external_id: string | null;
  name: string;
  address: Address | null;
  lat: number;
  lng: number;
  setup_time: number;
  setup_cost: number;
}

/** One line, the way the incumbent renders a depot address. */
function formatAddress(a: Address | null): string {
  if (!a) return '';
  return [a.line1, a.city, [a.region, a.postcode].filter(Boolean).join(' '), a.country]
    .filter((part) => part && String(part).trim())
    .join(', ');
}

/**
 * Parse the single-line form back into parts.
 *
 * The incumbent presents an address as one editable line, so we do too. Country
 * is kept whole rather than guessed at: splitting on commas is enough for the
 * US-format addresses in use, and anything it cannot resolve stays in line1
 * rather than being silently dropped.
 */
function parseAddress(text: string): Address {
  const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return {};
  const country = parts.length >= 4 ? parts.pop() ?? null : null;
  const regionPost = parts.length >= 3 ? parts.pop() ?? '' : '';
  const city = parts.length >= 2 ? parts.pop() ?? null : null;
  const m = /^([A-Za-z]{2})\s+(\S+)$/.exec(regionPost);
  return {
    line1: parts.join(', ') || null,
    city,
    region: m ? m[1] : regionPost || null,
    postcode: m ? m[2] : null,
    country,
  };
}

function DepotsPage({ territoryId }: { territoryId: string }) {
  const ui = useUi();
  const { me } = useAuth();
  const [depots, setDepots] = useState<Depot[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Depot | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    const { depots: list } = await api.depots(territoryId);
    setDepots(list as Depot[]);
    return list as Depot[];
  }, [territoryId]);

  useEffect(() => {
    void load().then((list) => {
      if (list[0]) {
        setSelected(list[0].id);
        setDraft(list[0]);
      }
    });
  }, [load]);

  const select = (d: Depot) => {
    setSelected(d.id);
    setDraft({ ...d });
    setEditing(false);
  };

  const create = async (body: Record<string, unknown>) => {
    try {
      const created = (await api.createDepot(territoryId, body)) as { id?: string };
      setNewOpen(false);
      const list = await load();
      const target = list.find((d) => d.id === created.id) ?? list[0];
      if (target) select(target);
      ui.notify('ok', 'Depot added');
    } catch (err) {
      ui.notify(
        'error',
        'Could not add the depot',
        err instanceof ApiError ? err.message : String(err),
      );
    }
  };

  const remove = async () => {
    if (!draft) return;
    const ok = await ui.confirm({
      title: `Delete ${draft.name}?`,
      body: 'Vehicles starting or ending here lose their location and will be planned from their first stop.',
      confirmLabel: 'Delete depot',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteDepot(territoryId, draft.id);
      const list = await load();
      if (list[0]) select(list[0]);
      else {
        setSelected(null);
        setDraft(null);
      }
      ui.notify('ok', 'Depot deleted');
    } catch (err) {
      ui.notify('error', 'Could not delete', err instanceof ApiError ? err.message : String(err));
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      /*
       * Send the position only if it was edited by hand. Otherwise leave it out
       * so the server derives it from the address, which is the field people
       * actually work in.
       */
      const original = depots.find((d) => d.id === draft.id);
      const movedByHand =
        original && (Number(original.lat) !== Number(draft.lat) || Number(original.lng) !== Number(draft.lng));

      const result = (await api.updateDepot(territoryId, draft.id, {
        name: draft.name,
        address: draft.address,
        ...(movedByHand ? { location: { lat: Number(draft.lat), lng: Number(draft.lng) } } : {}),
        setupTime: draft.setup_time,
        setupCost: Number(draft.setup_cost),
      })) as { located?: boolean };

      if (result?.located === false) {
        ui.notify(
          'error',
          'Address saved, position unchanged',
          me.geocoding
            ? 'That address could not be found on the map, so the position still points at the old one. Check the address, or enter coordinates under Advanced.'
            : 'Address lookup is not set up, so the map position still points at the old address. Enter coordinates under Advanced.',
        );
      } else {
        ui.notify('ok', 'Depot saved');
      }
      setEditing(false);
      const list = await load();
      const fresh = list.find((d) => d.id === draft.id);
      if (fresh) setDraft({ ...fresh });
    } catch (err) {
      ui.notify('error', 'Could not save depot', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="master-detail">
      <div className="master">
        <div className="side-head">
          <h2>Depots</h2>
          <span className="count-pill">{depots.length}</span>
          <IconButton small label="New depot" onClick={() => setNewOpen(true)}>
            <PlusIcon />
          </IconButton>
        </div>
        <div className="side-list">
          {depots.map((d) => (
            <button
              key={d.id}
              type="button"
              className="order-row"
              data-selected={d.id === selected ? 'true' : undefined}
              onClick={() => select(d)}
            >
              <b>{d.name}</b>
              {d.setup_time > 0 ? (
                <span className="delta" data-tone="warn">
                  {Math.round(d.setup_time / 60)}m setup
                </span>
              ) : null}
              <small>{formatAddress(d.address) || d.external_id || '-'}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="detail">
        {draft ? (
          <SettingsCard
            title={draft.name}
            description="Where a vehicle starts and ends its day."
            editing={editing}
            onEdit={() => setEditing(true)}
            onCancel={() => {
              setEditing(false);
              const original = depots.find((d) => d.id === draft.id);
              if (original) setDraft({ ...original });
            }}
            onSave={() => void save()}
            saving={saving}
            actions={
              <IconButton tone="danger" label="Delete depot" onClick={() => void remove()}>
                <TrashIcon />
              </IconButton>
            }
          >
            <Field label="Name">
              <TextInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
            </Field>
            <Field label="Address" wide>
              <TextInput
                value={formatAddress(draft.address)}
                placeholder="1 Example St, Cincinnati, OH 45202, United States"
                onChange={(v) => setDraft({ ...draft, address: parseAddress(v) })}
              />
            </Field>
            <Field label="Setup cost">
              <NumberInput
                value={Number(draft.setup_cost)}
                min={0}
                step={0.01}
                onChange={(v) => setDraft({ ...draft, setup_cost: v })}
              />
            </Field>
            <Field label="Setup time">
              <NumberInput
                value={Math.round(draft.setup_time / 60)}
                min={0}
                suffix="min"
                onChange={(v) => setDraft({ ...draft, setup_time: v * 60 })}
              />
            </Field>

            <Disclosure label="Advanced">
              <p className="disclosure-note">
                Position is derived from the address. Adjust it only to correct a pin the
                geocoder placed badly.
              </p>
              <Field label="Latitude">
                <NumberInput
                  value={Number(draft.lat)}
                  step={0.000001}
                  onChange={(v) => setDraft({ ...draft, lat: v })}
                />
              </Field>
              <Field label="Longitude">
                <NumberInput
                  value={Number(draft.lng)}
                  step={0.000001}
                  onChange={(v) => setDraft({ ...draft, lng: v })}
                />
              </Field>
            </Disclosure>
          </SettingsCard>
        ) : (
          <div className="empty">Select a depot.</div>
        )}
      </div>

      <NewDepotDrawer
        open={newOpen}
        onClose={() => setNewOpen(false)}
        near={depots[0] ?? null}
        onCreate={(body) => void create(body)}
      />
    </div>
  );
}

/**
 * Creating a depot.
 *
 * A position is required, and until a geocoder is configured nothing derives it
 * from the address, so the form starts the new depot at an existing one and
 * says so rather than dropping a pin in the ocean.
 */
function NewDepotDrawer({
  open,
  onClose,
  near,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  near: Depot | null;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState(Number(near?.lat ?? 0));
  const [lng, setLng] = useState(Number(near?.lng ?? 0));
  /* Coordinates typed by hand. Only then are they sent: otherwise the address decides. */
  const [manual, setManual] = useState(false);
  const geocoding = useAuth().me.geocoding === true;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New depot"
      subtitle="A start or end location for a vehicle"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim()}
            onClick={() =>
              onCreate({
                name: name.trim(),
                address: address.trim() ? parseAddress(address) : null,
                ...(manual || !geocoding || !address.trim() ? { location: { lat, lng } } : {}),
              })
            }
          >
            Create depot
          </button>
        </>
      }
    >
      <Field label="Name">
        <TextInput value={name} onChange={setName} placeholder="Carlos home" />
      </Field>
      <Field label="Address" wide>
        <TextInput
          value={address}
          onChange={setAddress}
          placeholder="1 Example St, Cincinnati, OH 45202, United States"
        />
      </Field>
      <Disclosure label="Advanced" defaultOpen={!geocoding && !near}>
        <p className="disclosure-note">
          {geocoding ? (
            manual ? (
              'These coordinates will be used instead of the address.'
            ) : (
              'The position comes from the address. Enter coordinates only to place it somewhere the address does not describe.'
            )
          ) : (
            <>
              Address lookup is not set up, so the position starts
              {near ? ` at ${near.name}` : ' at zero'} and needs correcting here.
            </>
          )}
        </p>
        <Field label="Latitude">
          <NumberInput value={lat} step={0.000001} onChange={(v) => { setLat(v); setManual(true); }} />
        </Field>
        <Field label="Longitude">
          <NumberInput value={lng} step={0.000001} onChange={(v) => { setLng(v); setManual(true); }} />
        </Field>
      </Disclosure>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ *
 * Vehicles
 * ------------------------------------------------------------------ */

/* Miles, because that is the unit the territory works in and displays. */
const KM_PER_MILE = 1.609344;

/*
 * Our listbox carries string values, and "no driver" is a real choice rather
 * than the absence of one, so it needs a value of its own. Anything that
 * reaches the API is translated back to null.
 */
const UNASSIGNED = '__none__';

/** Postgres hands dates back as Date or string depending on the driver. */
function isoOf(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).slice(0, 10);
}

interface Vehicle {
  id: string;
  external_id: string | null;
  name: string;
  technician_id: string | null;
  technician_name: string | null;
  start_depot_id: string | null;
  end_depot_id: string | null;
  capacity: Record<string, number>;
  notes: string | null;
  cost_per_service_hour: number | null;
  cost_per_idle_hour: number | null;
  cost_per_break_hour: number | null;
  shift_start: number;
  shift_end: number;
  break_start: number | null;
  break_end: number | null;
  break_duration: number;
  flexible_start: boolean;
  preparation_time: number;
  close_out_time: number;
  speed_factor: number;
  cost_per_km: number;
  cost_per_hour: number;
  cost_fixed: number;
  cost_per_stop: number;
  max_stops: number | null;
  max_working_time_s: number | null;
  min_working_time_s: number | null;
  max_driving_time_s: number | null;
  max_distance_m: number | null;
  max_orders: number | null;
  max_locations: number | null;
  tags: string[];
  active: boolean;
}

/*
 * A vehicle's routing settings are per-day, not per-vehicle.
 *
 * "Normal day" is what the vehicle columns hold; a calendar exception overrides
 * some of them on the dates its recurrence matches. Only the differences are
 * stored, so widening the normal working day still reaches every date that did
 * not explicitly disagree with it.
 */
interface DaySettings {
  available: boolean;
  shiftStart: number;
  shiftEnd: number;
  flexibleStart: boolean;
  preparationTime: number;
  closeOutTime: number;
  breakStart: number | null;
  breakEnd: number | null;
  breakDuration: number;
  maxStops: number | null;
  speedFactor: number;
}

type ExceptionRow = Record<string, unknown> & { id: string };

function normalDay(v: Vehicle): DaySettings {
  return {
    available: v.active,
    shiftStart: v.shift_start,
    shiftEnd: v.shift_end,
    flexibleStart: v.flexible_start,
    preparationTime: v.preparation_time,
    closeOutTime: v.close_out_time,
    breakStart: v.break_start,
    breakEnd: v.break_end,
    breakDuration: v.break_duration,
    maxStops: v.max_stops,
    speedFactor: Number(v.speed_factor),
  };
}

function exceptionDay(v: Vehicle, e: ExceptionRow): DaySettings {
  const base = normalDay(v);
  const over = (e.settings ?? {}) as Partial<DaySettings>;
  return { ...base, ...over };
}

/** Only what this day disagrees with, which is what an override means. */
function dayOverrides(base: DaySettings, day: DaySettings): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(base) as (keyof DaySettings)[]) {
    if (day[key] !== base[key]) out[key] = day[key];
  }
  return out;
}

function exceptionLabel(e: ExceptionRow): string {
  const name = e.name ? String(e.name) : '';
  const base = name || describeRecurrence(e);
  /*
   * What the exception does, not only when it applies.
   *
   * A chip reading "1 October" says a day is special and leaves the dispatcher
   * to open it to find out how. Where the answer is "this vehicle is not
   * working", that is the whole point of the row and belongs on its face --
   * an operator scanning the strip can see at once which days are days off.
   */
  const settings = (e.settings ?? {}) as Record<string, unknown>;
  return settings.available === false ? `${base} · off the road` : base;
}

function VehiclesPage({ territoryId }: { territoryId: string }) {
  const ui = useUi();
  /*
   * The account's formats. Every suffix on this page used to say `USD` and
   * every distance `mi` regardless of what the account was configured for, so
   * an operator in kilometres was typing into a field labelled in miles.
   */
  const { distance_unit: unit, currency } = useAuth().me.account;
  const perDistance = `${currency}/${unit}`;
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selected, setSelected] = useState<Vehicle | null>(null);
  const [draft, setDraft] = useState<Vehicle | null>(null);
  const [tab, setTab] = useState<'general' | 'routing'>('routing');
  const [exceptions, setExceptions] = useState<ExceptionRow[]>([]);
  /* null means the normal day; otherwise the exception being looked at. */
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [day, setDay] = useState<DaySettings | null>(null);
  const [depots, setDepots] = useState<{ id: string; name: string }[]>([]);
  const [technicians, setTechnicians] = useState<{ id: string; name: string }[]>([]);
  const [allRegions, setAllRegions] = useState<{ id: string; name: string; kind: string }[]>([]);
  /* This vehicle's rules, keyed by region: absent means no restriction. */
  const [regionRules, setRegionRules] = useState<Record<string, 'allow' | 'deny'>>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newExceptionOpen, setNewExceptionOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFromOpen, setCopyFromOpen] = useState(false);
  const [newVehicleOpen, setNewVehicleOpen] = useState(false);

  const load = useCallback(async () => {
    const [{ vehicles: list }, { depots: depotList }, { technicians: techList }, regionResult] =
      await Promise.all([
        api.vehicles(territoryId),
        api.depots(territoryId),
        api.technicians(territoryId),
        api.regions(territoryId).catch(() => ({ regions: [] })),
      ]);
    setVehicles(list as Vehicle[]);
    setDepots(depotList as { id: string; name: string }[]);
    setTechnicians(techList as { id: string; name: string }[]);
    setAllRegions(
      (regionResult.regions as { id: string; name: string; kind: string; archived?: boolean }[])
        .filter((r) => !r.archived),
    );
    return list as Vehicle[];
  }, [territoryId]);

  const loadExceptions = useCallback(
    async (vehicleId: string) => {
      try {
        const { exceptions: list } = await api.vehicleExceptions(territoryId, vehicleId);
        setExceptions(list as ExceptionRow[]);
        return list as ExceptionRow[];
      } catch {
        setExceptions([]);
        return [];
      }
    },
    [territoryId],
  );

  const select = useCallback(
    async (v: Vehicle) => {
      setSelected(v);
      setDraft({ ...v });
      setEditing(false);
      setCalendarId(null);
      setDay(normalDay(v));
      try {
        const { regions } = await api.vehicleRegions(territoryId, v.id);
        setRegionRules(
          Object.fromEntries(
            (regions as { id: string; mode: 'allow' | 'deny' }[]).map((r) => [r.id, r.mode]),
          ),
        );
      } catch {
        setRegionRules({});
      }
      await loadExceptions(v.id);
    },
    [territoryId, loadExceptions],
  );

  useEffect(() => {
    void load().then((list) => {
      if (list[0]) void select(list[0]);
    });
  }, [load, select]);

  /* Switching calendars re-derives the form from whichever day is now shown. */
  const showCalendar = (id: string | null) => {
    if (!selected) return;
    setCalendarId(id);
    setEditing(false);
    setDraft({ ...selected });
    const e = id ? exceptions.find((x) => x.id === id) : null;
    setDay(e ? exceptionDay(selected, e) : normalDay(selected));
  };

  const cancel = () => {
    setEditing(false);
    showCalendar(calendarId);
  };

  const currentException = calendarId ? exceptions.find((e) => e.id === calendarId) ?? null : null;

  const save = async () => {
    if (!draft || !day || !selected) return;
    setSaving(true);
    try {
      if (currentException) {
        /* An exception carries only its differences from the normal day. */
        await api.updateVehicleException(territoryId, draft.id, currentException.id, {
          recurrence: currentException.recurrence,
          name: currentException.name ?? null,
          onDate: isoOf(currentException.on_date),
          weekday: currentException.weekday ?? null,
          dayOfMonth: currentException.day_of_month ?? null,
          month: currentException.month ?? null,
          settings: dayOverrides(normalDay(selected), day),
        });
        ui.notify('ok', 'Calendar exception saved');
        const list = await loadExceptions(draft.id);
        const fresh = list.find((e) => e.id === currentException.id);
        if (fresh) setDay(exceptionDay(selected, fresh));
      } else {
        await api.updateVehicle(territoryId, draft.id, {
          name: draft.name,
          technicianId: draft.technician_id,
          shiftStart: day.shiftStart,
          shiftEnd: day.shiftEnd,
          breakStart: day.breakStart,
          breakEnd: day.breakEnd,
          breakDuration: day.breakDuration,
          flexibleStart: day.flexibleStart,
          preparationTime: day.preparationTime,
          closeOutTime: day.closeOutTime,
          speedFactor: day.speedFactor,
          maxStops: day.maxStops,
          maxWorkingTimeSec: draft.max_working_time_s,
          minWorkingTimeSec: draft.min_working_time_s,
          maxDrivingTimeSec: draft.max_driving_time_s,
          maxDistanceMt: draft.max_distance_m,
          maxOrders: draft.max_orders,
          maxLocations: draft.max_locations,
          active: day.available,
          costPerKm: Number(draft.cost_per_km),
          costPerHour: Number(draft.cost_per_hour),
          costFixed: Number(draft.cost_fixed),
          costPerStop: Number(draft.cost_per_stop),
          costPerServiceHour: draft.cost_per_service_hour,
          costPerIdleHour: draft.cost_per_idle_hour,
          costPerBreakHour: draft.cost_per_break_hour,
          tags: draft.tags,
          notes: draft.notes,
          startDepotId: draft.start_depot_id,
          endDepotId: draft.end_depot_id,
          capacity: draft.capacity ?? {},
        });
        await api.saveVehicleRegions(
          territoryId,
          draft.id,
          Object.entries(regionRules).map(([regionId, mode]) => ({ regionId, mode })),
        );
        ui.notify('ok', 'Vehicle saved');
        const list = await load();
        const fresh = list.find((v) => v.id === draft.id);
        if (fresh) {
          setSelected(fresh);
          setDraft({ ...fresh });
          setDay(normalDay(fresh));
        }
      }
      setEditing(false);
    } catch (err) {
      ui.notify('error', 'Could not save', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const createException = async (body: Record<string, unknown>) => {
    if (!draft) return;
    try {
      const created = (await api.createVehicleException(territoryId, draft.id, body)) as
        | ExceptionRow
        | undefined;
      setNewExceptionOpen(false);
      const list = await loadExceptions(draft.id);
      const target = created?.id ? list.find((e) => e.id === created.id) : undefined;
      if (target && selected) {
        setCalendarId(target.id);
        setDay(exceptionDay(selected, target));
        setEditing(false);
      }
      ui.notify('ok', 'Calendar exception added');
    } catch (err) {
      ui.notify(
        'error',
        'Could not add the exception',
        err instanceof ApiError ? err.message : String(err),
      );
    }
  };

  const removeException = async () => {
    if (!draft || !currentException) return;
    const ok = await ui.confirm({
      title: `Delete ${exceptionLabel(currentException)}?`,
      body: 'The vehicle goes back to its normal day on those dates. Plans already built keep the routes they were given.',
      confirmLabel: 'Delete exception',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteVehicleException(territoryId, draft.id, currentException.id);
      await loadExceptions(draft.id);
      showCalendar(null);
      ui.notify('ok', 'Calendar exception deleted');
    } catch (err) {
      ui.notify('error', 'Could not delete', err instanceof ApiError ? err.message : String(err));
    }
  };

  const removeVehicle = async () => {
    if (!draft) return;
    const ok = await ui.confirm({
      title: `Delete ${draft.name}?`,
      body: 'The vehicle disappears from planning. Routes it already ran keep their history.',
      confirmLabel: 'Delete vehicle',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteVehicle(territoryId, draft.id);
      const list = await load();
      if (list[0]) await select(list[0]);
      else {
        setSelected(null);
        setDraft(null);
      }
      ui.notify('ok', 'Vehicle deleted');
    } catch (err) {
      ui.notify('error', 'Could not delete', err instanceof ApiError ? err.message : String(err));
    }
  };

  const createVehicle = async (body: Record<string, unknown>) => {
    try {
      const created = (await api.createVehicle(territoryId, body)) as { id?: string };
      setNewVehicleOpen(false);
      const list = await load();
      const target = list.find((v) => v.id === created.id) ?? list[0];
      if (target) await select(target);
      ui.notify('ok', 'Vehicle added');
    } catch (err) {
      ui.notify('error', 'Could not add the vehicle', err instanceof ApiError ? err.message : String(err));
    }
  };

  /*
   * Copy exactly the four fields the incumbent's dialog copies -- preparation
   * time, close out time, and the working day's start and end -- confirmed
   * against their screen on 2026-09-04.
   *
   * We were copying nine, which quietly carried breaks, speed and max stops
   * onto vehicles nobody meant to change. Copying more than they do is not a
   * bonus here: a dispatcher who learned the incumbent's dialog would use ours
   * expecting the same blast radius.
   */
  const copySettingsTo = async (ids: string[]) => {
    if (!day) return;
    try {
      for (const id of ids) {
        await api.updateVehicle(territoryId, id, {
          shiftStart: day.shiftStart,
          shiftEnd: day.shiftEnd,
          preparationTime: day.preparationTime,
          closeOutTime: day.closeOutTime,
        });
      }
      setCopyOpen(false);
      await load();
      ui.notify('ok', `Settings copied to ${ids.length} vehicle${ids.length === 1 ? '' : 's'}`);
    } catch (err) {
      ui.notify('error', 'Could not copy', err instanceof ApiError ? err.message : String(err));
    }
  };

  /**
   * Take selected groups of settings from another vehicle onto this one.
   *
   * One request with the fields of every ticked group merged together, so a
   * copy either lands whole or not at all -- half a cost sheet from one van and
   * half from another is worse than a failure.
   */
  const copyFromVehicle = async (sourceId: string, groups: CopyGroupId[]) => {
    const source = vehicles.find((v) => v.id === sourceId);
    if (!draft || !source) return;
    const picked = COPY_GROUPS.filter((g) => groups.includes(g.id));
    const body = picked.reduce(
      (acc, g) => ({ ...acc, ...g.fields(source) }),
      {} as Record<string, unknown>,
    );
    try {
      await api.updateVehicle(territoryId, draft.id, body);
      setCopyFromOpen(false);
      const list = await load();
      const fresh = list.find((v) => v.id === draft.id);
      if (fresh) {
        setSelected(fresh);
        setDraft({ ...fresh });
        setDay(normalDay(fresh));
      }
      ui.notify(
        'ok',
        `Copied from ${source.name}`,
        `${picked.map((g) => g.label.toLowerCase()).join(' and ')} now match ${source.name}.`,
      );
    } catch (err) {
      ui.notify(
        'error',
        'Could not copy those settings',
        err instanceof ApiError ? err.message : String(err),
      );
    }
  };

  const dayActions = (
    <>
      <IconButton label="New calendar exception" onClick={() => setNewExceptionOpen(true)}>
        <CalendarPlusIcon />
      </IconButton>
      <IconButton label="Copy settings to other vehicles" onClick={() => setCopyOpen(true)}>
        <CopyIcon />
      </IconButton>
      <IconButton
        label="Copy costs or constraints from another vehicle"
        onClick={() => setCopyFromOpen(true)}
      >
        <CopyIntoIcon />
      </IconButton>
      {currentException ? (
        <IconButton
          tone="danger"
          label="Delete this calendar exception"
          onClick={() => void removeException()}
        >
          <TrashIcon />
        </IconButton>
      ) : null}
    </>
  );

  return (
    <div className="master-detail">
      <div className="master">
        <div className="side-head">
          <h2>Vehicles</h2>
          <span className="count-pill">{vehicles.length}</span>
          <IconButton small label="New vehicle" onClick={() => setNewVehicleOpen(true)}>
            <PlusIcon />
          </IconButton>
        </div>
        <div className="side-list">
          {vehicles.map((v) => (
            <button
              key={v.id}
              type="button"
              className="order-row"
              data-selected={v.id === draft?.id ? 'true' : undefined}
              onClick={() => void select(v)}
            >
              <b>{v.name}</b>
              <span className="delta" data-tone={v.flexible_start ? 'ok' : 'idle'}>
                {v.flexible_start ? 'flex' : 'fixed'}
              </span>
              <small>
                {hhmm(v.shift_start)}-{hhmm(v.shift_end)}
                {v.tags.length > 0 ? ` · ${v.tags.join(', ')}` : ''}
              </small>
            </button>
          ))}
        </div>
      </div>

      <div className="detail">
        {draft && day ? (
          <>
            <div className="tabs">
              <button
                type="button"
                className="tab"
                aria-selected={tab === 'general'}
                onClick={() => setTab('general')}
              >
                General settings
              </button>
              <button
                type="button"
                className="tab"
                aria-selected={tab === 'routing'}
                onClick={() => setTab('routing')}
              >
                Routing settings
              </button>
            </div>

            {tab === 'general' ? (
              <SettingsCard
                title={draft.name}
                description="Identity and assignment."
                editing={editing}
                onEdit={() => setEditing(true)}
                onCancel={cancel}
                onSave={() => void save()}
                saving={saving}
                actions={
                  <IconButton
                    tone="danger"
                    label="Delete vehicle"
                    onClick={() => void removeVehicle()}
                  >
                    <TrashIcon />
                  </IconButton>
                }
              >
                <Field label="Name">
                  <TextInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
                </Field>
                <Field label="Reference">
                  <TextInput value={draft.external_id ?? ''} onChange={() => {}} disabled />
                </Field>
                <Field label="Driver">
                  <Select
                    value={draft.technician_id ?? UNASSIGNED}
                    options={[
                      { value: UNASSIGNED, label: 'Unassigned' },
                      ...technicians.map((t) => ({ value: t.id, label: t.name })),
                    ]}
                    onChange={(v) =>
                      setDraft({ ...draft, technician_id: v === UNASSIGNED ? null : v })
                    }
                  />
                </Field>
                <Field label="Available">
                  <Toggle
                    checked={day.available}
                    onChange={(v) => setDay({ ...day, available: v })}
                    tone={day.available ? 'ok' : 'bad'}
                    label={
                      day.available
                        ? 'Yes — vehicle will be included in the route'
                        : 'No — vehicle will not be included in the route'
                    }
                  />
                </Field>
              </SettingsCard>
            ) : (
              <>
                {/*
                 * The calendar strip. The normal day sits first and every
                 * exception beside it, so which day is on screen is never in
                 * doubt -- the same mistake as editing the wrong date.
                 */}
                <div className="calendar-strip">
                  <button
                    type="button"
                    className="chip"
                    aria-pressed={calendarId === null}
                    onClick={() => showCalendar(null)}
                  >
                    Normal day
                  </button>
                  {exceptions.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      className="chip"
                      aria-pressed={calendarId === e.id}
                      onClick={() => showCalendar(e.id)}
                    >
                      {exceptionLabel(e)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="chip chip-add"
                    onClick={() => setNewExceptionOpen(true)}
                  >
                    + New exception
                  </button>
                </div>

                <SettingsCard
                  title={currentException ? exceptionLabel(currentException) : 'Normal day'}
                  description={
                    currentException
                      ? `Overrides the normal day ${describeRecurrence(currentException)}. Only the fields you change here are stored.`
                      : exceptions.length > 0
                        ? `Applies unless one of ${exceptions.length} calendar exception${exceptions.length === 1 ? '' : 's'} takes over.`
                        : 'Applies every day unless a calendar exception overrides it.'
                  }
                  editing={editing}
                  onEdit={() => setEditing(true)}
                  onCancel={cancel}
                  onSave={() => void save()}
                  saving={saving}
                  actions={dayActions}
                >
                  {/*
                    * Says what the setting does, not whether it is on.
                    *
                    * "Yes" and "No" answer the label and nothing else, and the
                    * consequence -- whether this van gets work -- is the whole
                    * reason anyone touches the field. An operator took a
                    * vehicle off the road, saw the right words on the wrong
                    * state, and it kept getting routed; the words now name the
                    * outcome and the colour shows which way it is set without
                    * reading them.
                    */}
                  <Field label="Vehicle is available">
                    <Toggle
                      checked={day.available}
                      onChange={(v) => setDay({ ...day, available: v })}
                      tone={day.available ? 'ok' : 'bad'}
                      label={
                        day.available
                          ? 'Yes — vehicle will be included in the route'
                          : 'No — vehicle will not be included in the route'
                      }
                    />
                  </Field>

                  {currentException ? null : (
                    <>
                      <Field label="Start location">
                        <Select
                          value={draft.start_depot_id ?? UNASSIGNED}
                          options={[
                            { value: UNASSIGNED, label: 'None' },
                            ...depots.map((d) => ({ value: d.id, label: d.name })),
                          ]}
                          onChange={(v) =>
                            setDraft({ ...draft, start_depot_id: v === UNASSIGNED ? null : v })
                          }
                        />
                      </Field>
                      <Field label="Preparation time">
                        <NumberInput
                          value={Math.round(day.preparationTime / 60)}
                          min={0}
                          suffix="min"
                          onChange={(v) => setDay({ ...day, preparationTime: v * 60 })}
                        />
                      </Field>
                      <Field label="End location">
                        <Select
                          value={draft.end_depot_id ?? UNASSIGNED}
                          options={[
                            { value: UNASSIGNED, label: 'None' },
                            ...depots.map((d) => ({ value: d.id, label: d.name })),
                          ]}
                          onChange={(v) =>
                            setDraft({ ...draft, end_depot_id: v === UNASSIGNED ? null : v })
                          }
                        />
                      </Field>
                      <Field label="Close out time">
                        <NumberInput
                          value={Math.round(day.closeOutTime / 60)}
                          min={0}
                          suffix="min"
                          onChange={(v) => setDay({ ...day, closeOutTime: v * 60 })}
                        />
                      </Field>
                    </>
                  )}

                  <h3 className="section-label">Working hours</h3>
                  <Field
                    label="Flexible working day start"
                    hint="Leave later rather than idle at the first stop"
                  >
                    <Toggle
                      checked={day.flexibleStart}
                      onChange={(v) => setDay({ ...day, flexibleStart: v })}
                    />
                  </Field>
                  <Field label="Working day start">
                    <TimeInput
                      seconds={day.shiftStart}
                      onChange={(v) => setDay({ ...day, shiftStart: v ?? 0 })}
                    />
                  </Field>
                  <Field label="Working day end" hint="Route might end before this time">
                    <TimeInput
                      seconds={day.shiftEnd}
                      onChange={(v) => setDay({ ...day, shiftEnd: v ?? 86400 })}
                    />
                  </Field>

                  <h3 className="section-label">Breaks</h3>
                  <Field label="Break window">
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <TimeInput
                        seconds={day.breakStart}
                        onChange={(v) => setDay({ ...day, breakStart: v })}
                      />
                      <span style={{ color: 'var(--text-3)' }}>to</span>
                      <TimeInput
                        seconds={day.breakEnd}
                        onChange={(v) => setDay({ ...day, breakEnd: v })}
                      />
                    </span>
                  </Field>
                  <Field label="Break length">
                    <NumberInput
                      value={Math.round(day.breakDuration / 60)}
                      min={0}
                      suffix="min"
                      onChange={(v) => setDay({ ...day, breakDuration: v * 60 })}
                    />
                  </Field>

                  {currentException ? null : (
                    <>
                      <h3 className="section-label">Accessible regions</h3>
                      <RegionRules
                        regions={allRegions}
                        rules={regionRules}
                        onChange={setRegionRules}
                      />
                    </>
                  )}

                  {/*
                    * Their Constraints section, field for field and in their
                    * order: the three counts, then distance, then the two
                    * times. Labels are bare nouns -- the heading carries the
                    * "max" -- and the units are hours and miles because that is
                    * how theirs are labelled. Storage stays seconds and metres.
                    *
                    * Zero throughout means "no limit", which is how their own
                    * fields read when left blank. A limit of zero would forbid
                    * the vehicle from working at all, and nobody means that.
                    *
                    * `min_working_time_s` is deliberately absent. Their API
                    * carries it; their form does not, and a field of ours that
                    * theirs lacks is exactly the drift this whole exercise is
                    * meant to stop. The column stays for the compat surface.
                    */}
                  <h3 className="section-label">Constraints</h3>

                  {currentException ? null : (
                    <>
                      <Field label="Orders" hint="0 for no limit">
                        <NumberInput
                          value={draft.max_orders ?? 0}
                          min={0}
                          onChange={(v) => setDraft({ ...draft, max_orders: v > 0 ? v : null })}
                        />
                      </Field>
                    </>
                  )}

                  <Field label="Stops" hint="0 for no limit">
                    <NumberInput
                      value={day.maxStops ?? 0}
                      min={0}
                      onChange={(v) => setDay({ ...day, maxStops: v > 0 ? v : null })}
                    />
                  </Field>

                  {currentException ? null : (
                    <>
                      <Field label="Locations" hint="Orders at one address count once">
                        <NumberInput
                          value={draft.max_locations ?? 0}
                          min={0}
                          onChange={(v) => setDraft({ ...draft, max_locations: v > 0 ? v : null })}
                        />
                      </Field>
                      <Field label="Mileage" hint="0 for no limit">
                        <NumberInput
                          value={
                            draft.max_distance_m === null
                              ? 0
                              : Math.round(fromMeters(Number(draft.max_distance_m), unit))
                          }
                          min={0}
                          suffix={unit}
                          onChange={(v) =>
                            setDraft({ ...draft, max_distance_m: v > 0 ? toMeters(v, unit) : null })
                          }
                        />
                      </Field>
                      <Field label="Driving Hours" hint="0 for no limit">
                        <NumberInput
                          value={hoursOf(draft.max_driving_time_s)}
                          min={0}
                          step={0.25}
                          suffix="h"
                          onChange={(v) =>
                            setDraft({ ...draft, max_driving_time_s: v > 0 ? Math.round(v * 3600) : null })
                          }
                        />
                      </Field>
                      <Field label="Working Hours" hint="0 for no limit">
                        <NumberInput
                          value={hoursOf(draft.max_working_time_s)}
                          min={0}
                          step={0.25}
                          suffix="h"
                          onChange={(v) =>
                            setDraft({ ...draft, max_working_time_s: v > 0 ? Math.round(v * 3600) : null })
                          }
                        />
                      </Field>
                    </>
                  )}

                  {currentException ? null : (
                    <>
                      <h3 className="section-label">Loads</h3>
                      <Field label="Capacity" hint="name=amount, comma separated">
                        <TextInput
                          value={Object.entries(draft.capacity ?? {})
                            .map(([k, v]) => `${k}=${v}`)
                            .join(', ')}
                          placeholder="jobs=12"
                          onChange={(v) => {
                            const capacity: Record<string, number> = {};
                            for (const part of v.split(',')) {
                              const [k, n] = part.split('=').map((x) => x.trim());
                              if (k && n && Number.isFinite(Number(n))) capacity[k] = Number(n);
                            }
                            setDraft({ ...draft, capacity });
                          }}
                        />
                      </Field>

                      <h3 className="section-label">Tags</h3>
                      <Field label="Tags">
                        <TagsInput
                          value={draft.tags}
                          placeholder="senior tech, sealed system"
                          onChange={(tags) => setDraft({ ...draft, tags })}
                        />
                      </Field>
                    </>
                  )}

                  {/*
                    * A slider with three named stops, as theirs is. The values
                    * are the same three a select held; the control is not, and
                    * the difference is real -- a slider says "this is a dial
                    * with a middle", a dropdown says "three unrelated options".
                    */}
                  <h3 className="section-label">Speed</h3>
                  <Field label="Speed">
                    <StopSlider
                      label="Travel speed for this vehicle"
                      value={String(day.speedFactor)}
                      stops={[
                        { value: '0.5', label: 'Half' },
                        { value: '1', label: 'Normal' },
                        { value: '2', label: '2x' },
                      ]}
                      onChange={(v) => setDay({ ...day, speedFactor: Number(v) })}
                    />
                  </Field>

                  {currentException ? null : (
                    <>
                      <h3 className="section-label">Costs</h3>
                      <Field label="Fixed" hint="Charged if the vehicle is used at all">
                        <NumberInput
                          value={Number(draft.cost_fixed)}
                          min={0}
                          step={0.01}
                          suffix={currency}
                          onChange={(v) => setDraft({ ...draft, cost_fixed: v })}
                        />
                      </Field>
                      <Field label="Service Time">
                        <NumberInput
                          value={Number(draft.cost_per_service_hour ?? draft.cost_per_hour)}
                          min={0}
                          step={0.01}
                          suffix={`${currency}/hr`}
                          onChange={(v) => setDraft({ ...draft, cost_per_service_hour: v })}
                        />
                      </Field>
                      <Field label="Driving Time">
                        <NumberInput
                          value={Number(draft.cost_per_hour)}
                          min={0}
                          step={0.01}
                          suffix={`${currency}/hr`}
                          onChange={(v) => setDraft({ ...draft, cost_per_hour: v })}
                        />
                      </Field>
                      <Field label="Idle Time">
                        <NumberInput
                          value={Number(draft.cost_per_idle_hour ?? draft.cost_per_hour)}
                          min={0}
                          step={0.01}
                          suffix={`${currency}/hr`}
                          onChange={(v) => setDraft({ ...draft, cost_per_idle_hour: v })}
                        />
                      </Field>
                      <Field label="Break Time">
                        <NumberInput
                          value={Number(draft.cost_per_break_hour ?? draft.cost_per_hour)}
                          min={0}
                          step={0.01}
                          suffix={`${currency}/hr`}
                          onChange={(v) => setDraft({ ...draft, cost_per_break_hour: v })}
                        />
                      </Field>
                      {/*
                        * `Per mile` on a miles account, `Per kilometre` on a
                        * kilometres one -- theirs follows the unit, and the
                        * stored column is per kilometre either way.
                        */}
                      <Field
                        label={unit === 'mi' ? 'Per mile' : 'Per kilometre'}
                        hint={unit === 'mi' ? 'Stored per kilometre and converted' : undefined}
                      >
                        <NumberInput
                          value={Number(
                            (unit === 'mi'
                              ? Number(draft.cost_per_km) * KM_PER_MILE
                              : Number(draft.cost_per_km)
                            ).toFixed(4),
                          )}
                          min={0}
                          step={0.01}
                          suffix={perDistance}
                          onChange={(v) =>
                            setDraft({ ...draft, cost_per_km: unit === 'mi' ? v / KM_PER_MILE : v })
                          }
                        />
                      </Field>
                      <Field label="Per Stop">
                        <NumberInput
                          value={Number(draft.cost_per_stop)}
                          min={0}
                          step={0.01}
                          suffix={currency}
                          onChange={(v) => setDraft({ ...draft, cost_per_stop: v })}
                        />
                      </Field>

                      <h3 className="section-label">Notes</h3>
                      <Field label="Notes" wide>
                        <TextArea
                          value={draft.notes ?? ''}
                          rows={3}
                          onChange={(v) => setDraft({ ...draft, notes: v || null })}
                        />
                      </Field>
                    </>
                  )}
                </SettingsCard>
              </>
            )}
          </>
        ) : (
          <div className="empty">Select a vehicle.</div>
        )}
      </div>

      <NewExceptionDrawer
        open={newExceptionOpen}
        onClose={() => setNewExceptionOpen(false)}
        onCreate={(body) => void createException(body)}
      />

      <CopySettingsDrawer
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        source={draft}
        vehicles={vehicles}
        onCopy={(ids) => void copySettingsTo(ids)}
      />

      <CopyFromVehicleDrawer
        open={copyFromOpen}
        onClose={() => setCopyFromOpen(false)}
        target={draft}
        vehicles={vehicles}
        onApply={(sourceId, groups) => void copyFromVehicle(sourceId, groups)}
      />

      <NewVehicleDrawer
        open={newVehicleOpen}
        onClose={() => setNewVehicleOpen(false)}
        depots={depots}
        technicians={technicians}
        onCreate={(body) => void createVehicle(body)}
      />
    </div>
  );
}

/**
 * Which regions a vehicle may work in.
 *
 * Listing nothing means unrestricted, which is what an empty box has to read
 * as -- the alternative, barred from everywhere, would silently empty a fleet
 * the first time someone drew a region. Listing anything confines the vehicle
 * to what it lists.
 */
function RegionRules({
  regions,
  rules,
  onChange,
}: {
  regions: { id: string; name: string; kind: string }[];
  rules: Record<string, 'allow' | 'deny'>;
  onChange: (next: Record<string, 'allow' | 'deny'>) => void;
}) {
  const editing = useEditing();
  const listed = regions.filter((r) => rules[r.id]);

  if (regions.length === 0) {
    return (
      <div className="empty" style={{ padding: '10px 0', textAlign: 'left' }}>
        No regions are defined for this territory, so nothing restricts this vehicle.
      </div>
    );
  }

  if (!editing) {
    return listed.length === 0 ? (
      <div className="empty" style={{ padding: '10px 0', textAlign: 'left' }}>
        No accessible regions. This vehicle can work anywhere in the territory.
      </div>
    ) : (
      <div className="tag-list">
        {listed.map((r) => (
          <div key={r.id} className="tag-row">
            <div className="tag-main">
              <b>{r.name}</b>
              <small className="mono">{r.kind}</small>
            </div>
            <span className="tag-usage">
              {rules[r.id] === 'deny' ? 'kept out' : 'allowed in'}
            </span>
            <span />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {regions.map((r) => (
        <Field key={r.id} label={r.name} hint={r.kind}>
          <Select
            value={rules[r.id] ?? UNASSIGNED}
            options={[
              { value: UNASSIGNED, label: 'No rule' },
              { value: 'allow', label: 'Allowed in' },
              { value: 'deny', label: 'Kept out' },
            ]}
            onChange={(mode) => {
              const next = { ...rules };
              if (mode === UNASSIGNED) delete next[r.id];
              else next[r.id] = mode as 'allow' | 'deny';
              onChange(next);
            }}
          />
        </Field>
      ))}
      <p className="disclosure-note">
        Allowing any region confines this vehicle to the regions it is allowed into. Leave every
        row on "No rule" for a vehicle that can go anywhere.
      </p>
    </>
  );
}

/**
 * Creating a calendar exception.
 *
 * The recurrence choices are the incumbent's: a single date, or a standing
 * yearly, monthly or weekly rule. Availability is offered here because taking a
 * vehicle off the road for a day is what most exceptions are for; everything
 * else is edited on the exception once it exists.
 */
function NewExceptionDrawer({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [recurrence, setRecurrence] = useState('date');
  const [name, setName] = useState('');
  const [onDate, setOnDate] = useState(today);
  const [weekday, setWeekday] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [month, setMonth] = useState(1);
  const [available, setAvailable] = useState(false);

  const submit = () => {
    onCreate({
      recurrence,
      name: name.trim() || null,
      onDate: recurrence === 'date' ? onDate : null,
      weekday: recurrence === 'weekly' ? weekday : null,
      dayOfMonth: recurrence === 'monthly' || recurrence === 'yearly' ? dayOfMonth : null,
      month: recurrence === 'yearly' ? month : null,
      settings: available ? {} : { available: false },
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New calendar exception"
      subtitle="A day, or a repeating rule, where this vehicle does not follow its normal settings"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit}>
            Create exception
          </button>
        </>
      }
    >
      <Field label="Name" hint="Optional. Shown on the calendar strip.">
        <TextInput value={name} onChange={setName} placeholder="Independence Day" />
      </Field>
      <Field label="Repeats">
        <Select
          value={recurrence}
          options={[
            { value: 'date', label: 'Date', hint: 'one day only' },
            { value: 'yearly', label: 'Yearly', hint: 'same day each year' },
            { value: 'monthly', label: 'Monthly', hint: 'same day each month' },
            { value: 'weekly', label: 'Weekly', hint: 'same weekday' },
          ]}
          onChange={setRecurrence}
        />
      </Field>

      {recurrence === 'date' ? (
        <Field label="Date">
          <DatePicker value={onDate} today={today} onChange={setOnDate} />
        </Field>
      ) : null}

      {recurrence === 'weekly' ? (
        <Field label="Weekday">
          <Select
            value={String(weekday)}
            options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))}
            onChange={(v) => setWeekday(Number(v))}
          />
        </Field>
      ) : null}

      {recurrence === 'monthly' || recurrence === 'yearly' ? (
        <Field label="Day of month">
          <NumberInput value={dayOfMonth} min={1} max={31} onChange={setDayOfMonth} />
        </Field>
      ) : null}

      {recurrence === 'yearly' ? (
        <Field label="Month">
          <Select
            value={String(month)}
            options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
            onChange={(v) => setMonth(Number(v))}
          />
        </Field>
      ) : null}

      {/*
        * Two named choices rather than a toggle.
        *
        * This was a switch labelled "Vehicle is available", sitting in its off
        * position and reading "No, off the road". Somebody taking a van off the
        * road saw an unlit switch and pressed it to make that happen -- which
        * set the vehicle *available*, and stored an exception that overrode
        * nothing. The chip appeared on the calendar, the van kept getting work,
        * and nothing anywhere said why.
        *
        * A toggle whose label describes its state reads, to anyone in a hurry,
        * like a button describing what pressing it will do. Naming both options
        * removes the guess.
        */}
      <Field label="On this day the vehicle is">
        <Select
          value={available ? 'working' : 'off'}
          options={[
            {
              value: 'off',
              label: 'Off the road',
              /* Same words as the vehicle's own availability field, so the two
               * places this is decided do not describe it differently. */
              hint: 'will not be included in the route',
            },
            {
              value: 'working',
              label: 'Working, with different settings',
              hint: 'included, but hours or limits differ',
            },
          ]}
          onChange={(v) => setAvailable(v === 'working')}
        />
      </Field>

      {/*
        * Said plainly, because an exception that changes nothing is now refused
        * and a dispatcher should know what the second option expects of them.
        */}
      {available ? (
        <p className="disclosure-note">
          Pick the day from the strip after creating it and change the hours or limits that
          differ. An exception that changes nothing is not saved.
        </p>
      ) : null}

      <p className="disclosure-note">
        A more specific rule wins: a single date beats a yearly rule, which beats monthly, which
        beats weekly.
      </p>
    </Drawer>
  );
}

/**
 * What a copy can bring across, in the groups a dispatcher thinks in.
 *
 * Deliberately not "everything": name, driver, depots, tags and the calendar
 * belong to the vehicle itself, and a copy that quietly reassigned a technician
 * or moved a van's depot would be the last time anybody used this.
 */
const COPY_GROUPS = [
  {
    id: 'costs' as const,
    label: 'Costs',
    hint: 'fixed, hourly, per km, per stop, and the service, idle and break rates',
    fields: (v: Vehicle) => ({
      costFixed: Number(v.cost_fixed),
      costPerHour: Number(v.cost_per_hour),
      costPerKm: Number(v.cost_per_km),
      costPerStop: Number(v.cost_per_stop),
      costPerServiceHour: v.cost_per_service_hour,
      costPerIdleHour: v.cost_per_idle_hour,
      costPerBreakHour: v.cost_per_break_hour,
    }),
  },
  {
    id: 'constraints' as const,
    label: 'Constraints',
    hint: 'max stops, orders, locations, working and driving time, distance, capacity',
    fields: (v: Vehicle) => ({
      maxStops: v.max_stops,
      maxOrders: v.max_orders,
      maxLocations: v.max_locations,
      maxWorkingTimeSec: v.max_working_time_s,
      minWorkingTimeSec: v.min_working_time_s,
      maxDrivingTimeSec: v.max_driving_time_s,
      maxDistanceMt: v.max_distance_m,
      capacity: v.capacity ?? {},
    }),
  },
  {
    id: 'day' as const,
    label: 'Working day',
    hint: 'shift start and end, breaks, preparation, close out, speed factor',
    fields: (v: Vehicle) => ({
      shiftStart: v.shift_start,
      shiftEnd: v.shift_end,
      breakStart: v.break_start,
      breakEnd: v.break_end,
      breakDuration: v.break_duration,
      flexibleStart: v.flexible_start,
      preparationTime: v.preparation_time,
      closeOutTime: v.close_out_time,
      speedFactor: Number(v.speed_factor),
    }),
  },
];

type CopyGroupId = (typeof COPY_GROUPS)[number]['id'];

/**
 * Set this vehicle up like another one.
 *
 * The other copy drawer on this page pushes the working day out to a set of
 * vehicles, which is the incumbent's and is right for "everybody starts at
 * seven now". This is the other direction and the commoner errand: a van has
 * been added, or re-rated, and should match one that is already correct.
 *
 * Nothing is ticked to begin with, and the summary says exactly what will be
 * overwritten before the button does anything.
 */
function CopyFromVehicleDrawer({
  open,
  onClose,
  target,
  vehicles,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  target: Vehicle | null;
  vehicles: Vehicle[];
  onApply: (sourceId: string, groups: CopyGroupId[]) => void;
}) {
  const [sourceId, setSourceId] = useState('');
  const [groups, setGroups] = useState<CopyGroupId[]>([]);

  const options = vehicles
    .filter((v) => v.id !== target?.id)
    .map((v) => ({ value: v.id, label: v.name }));
  const source = vehicles.find((v) => v.id === sourceId) ?? null;
  const ready = source !== null && groups.length > 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Copy from another vehicle"
      subtitle={target ? `Into ${target.name}` : undefined}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            onClick={() => source && onApply(source.id, groups)}
          >
            {ready ? `Copy from ${source.name}` : 'Copy'}
          </button>
        </>
      }
    >
      <Field label="Copy from">
        <Select
          value={sourceId}
          options={options.length > 0 ? options : [{ value: '', label: 'No other vehicles' }]}
          onChange={setSourceId}
        />
      </Field>

      <h3 className="section-label">What to copy</h3>
      <div className="tag-list">
        {COPY_GROUPS.map((g) => {
          const on = groups.includes(g.id);
          return (
            <button
              key={g.id}
              type="button"
              className="chip"
              aria-pressed={on}
              onClick={() =>
                setGroups((cur) => (on ? cur.filter((x) => x !== g.id) : [...cur, g.id]))
              }
            >
              {g.label}
            </button>
          );
        })}
      </div>
      {COPY_GROUPS.filter((g) => groups.includes(g.id)).map((g) => (
        <p key={g.id} className="disclosure-note">
          <b>{g.label}:</b> {g.hint}
        </p>
      ))}

      {/*
        * Said before the button is pressed rather than confirmed after. The
        * fields a copy does not touch are the ones somebody would be most
        * alarmed to lose, so they are named.
        */}
      <p className="disclosure-note">
        {target ? `${target.name}'s` : 'This vehicle’s'} name, driver, depots, tags, notes and
        calendar exceptions are not touched. Whatever you tick is overwritten outright.
      </p>
    </Drawer>
  );
}

/** Copy the working-day settings on screen onto other vehicles. */
function CopySettingsDrawer({
  open,
  onClose,
  source,
  vehicles,
  onCopy,
}: {
  open: boolean;
  onClose: () => void;
  source: Vehicle | null;
  vehicles: Vehicle[];
  onCopy: (ids: string[]) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const targets = vehicles.filter((v) => v.id !== source?.id);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Copy settings"
      subtitle={source ? `From ${source.name}` : undefined}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={picked.length === 0}
            onClick={() => onCopy(picked)}
          >
            Copy to {picked.length || 'no'} vehicle{picked.length === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      <p className="disclosure-note">
        Copies four fields from this vehicle: preparation time, close out time, and the working day
        start and end. Breaks, speed, max stops, costs, tags, locations and calendar exceptions stay
        where they are.
      </p>
      <p className="disclosure-note">
        {/*
          * Theirs arrives with every vehicle already ticked, so one stray click
          * rewrites the whole fleet's hours. We start with none selected -- the
          * one deliberate departure from their screen on this page, and the
          * reason it is called out here rather than left silent.
          */}
        Nothing is selected to begin with. Choose the vehicles to overwrite.
      </p>
      <div className="tag-list">
        {targets.map((v) => {
          const on = picked.includes(v.id);
          return (
            <button
              key={v.id}
              type="button"
              className="tag-row"
              data-selected={on ? 'true' : undefined}
              onClick={() =>
                setPicked((p) => (on ? p.filter((id) => id !== v.id) : [...p, v.id]))
              }
            >
              <div className="tag-main">
                <b>{v.name}</b>
                <small className="mono">
                  {hhmm(v.shift_start)}-{hhmm(v.shift_end)}
                </small>
              </div>
              <span className="tag-usage">{on ? 'selected' : ''}</span>
            </button>
          );
        })}
      </div>
    </Drawer>
  );
}

/** The minimum a vehicle needs to exist; the rest is edited afterwards. */
function NewVehicleDrawer({
  open,
  onClose,
  depots,
  technicians,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  depots: { id: string; name: string }[];
  technicians: { id: string; name: string }[];
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [technicianId, setTechnicianId] = useState<string>(UNASSIGNED);
  const [depotId, setDepotId] = useState<string>(UNASSIGNED);
  const [shiftStart, setShiftStart] = useState(8 * 3600);
  const [shiftEnd, setShiftEnd] = useState(17 * 3600);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New vehicle"
      subtitle="Everything else is on the routing tab once it exists"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim()}
            onClick={() =>
              onCreate({
                name: name.trim(),
                externalId: externalId.trim() || null,
                technicianId: technicianId === UNASSIGNED ? null : technicianId,
                startDepotId: depotId === UNASSIGNED ? null : depotId,
                endDepotId: depotId === UNASSIGNED ? null : depotId,
                shiftStart,
                shiftEnd,
              })
            }
          >
            Create vehicle
          </button>
        </>
      }
    >
      <Field label="Name">
        <TextInput value={name} onChange={setName} placeholder="Van 14" />
      </Field>
      <Field label="Reference" hint="The ID your other systems already use">
        <TextInput value={externalId} onChange={setExternalId} />
      </Field>
      <Field label="Driver">
        <Select
          value={technicianId}
          options={[
            { value: UNASSIGNED, label: 'Unassigned' },
            ...technicians.map((t) => ({ value: t.id, label: t.name })),
          ]}
          onChange={setTechnicianId}
        />
      </Field>
      <Field label="Start and end location">
        <Select
          value={depotId}
          options={[
            { value: UNASSIGNED, label: 'None' },
            ...depots.map((d) => ({ value: d.id, label: d.name })),
          ]}
          onChange={setDepotId}
        />
      </Field>
      <Field label="Working day start">
        <TimeInput seconds={shiftStart} onChange={(v) => setShiftStart(v ?? 0)} />
      </Field>
      <Field label="Working day end">
        <TimeInput seconds={shiftEnd} onChange={(v) => setShiftEnd(v ?? 86400)} />
      </Field>
    </Drawer>
  );
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function describeRecurrence(e: Record<string, unknown>): string {
  switch (e.recurrence) {
    case 'date':
      return shortDate(e.on_date);
    case 'weekly':
      return `every ${WEEKDAYS[Number(e.weekday)] ?? '?'}`;
    case 'monthly':
      return `day ${e.day_of_month} each month`;
    case 'yearly':
      return `${e.day_of_month}/${e.month} each year`;
    default:
      return '';
  }
}

/* ------------------------------------------------------------------ *
 * Drivers
 * ------------------------------------------------------------------ */

interface Technician {
  id: string;
  external_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  tags: string[];
  active: boolean;
  app_visible_days: number | null;
  app_upcoming_detail: string | null;
  app_allow_barcode_add: boolean | null;
  has_password?: boolean;
  password_set_at?: string | null;
  password_set_by?: string | null;
  app_last_login_at?: string | null;
  app_locked?: boolean;
  app_sessions?: number;
  push_devices?: number;
}

function DriversPage({ territoryId }: { territoryId: string }) {
  const ui = useUi();
  const [drivers, setDrivers] = useState<Technician[]>([]);
  const [selected, setSelected] = useState<Technician | null>(null);
  const [draft, setDraft] = useState<Technician | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  /* Who a new notification starts addressed to; null is everyone. */
  const [compose, setCompose] = useState<{ preselect: string[] | null } | null>(null);
  const [sentCount, setSentCount] = useState(0);

  const load = useCallback(async () => {
    const { technicians } = await api.technicians(territoryId);
    setDrivers(technicians as Technician[]);
    return technicians as Technician[];
  }, [territoryId]);

  const select = (d: Technician) => {
    setSelected(d);
    setDraft({ ...d });
    setEditing(false);
  };

  useEffect(() => {
    void load().then((list) => {
      if (list[0]) {
        setSelected(list[0]);
        setDraft({ ...list[0] });
        setEditing(false);
      }
    });
  }, [load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.updateTechnician(territoryId, draft.id, {
        name: draft.name,
        email: draft.email,
        phone: draft.phone,
        tags: draft.tags,
        active: draft.active,
        appVisibleDays: draft.app_visible_days,
        appUpcomingDetail: draft.app_upcoming_detail,
        appAllowBarcodeAdd: draft.app_allow_barcode_add,
      });
      ui.notify('ok', 'Driver saved');
      const list = await load();
      const fresh = list.find((d) => d.id === draft.id);
      if (fresh) {
        setSelected(fresh);
        setDraft({ ...fresh });
      }
      setEditing(false);
    } catch (err) {
      ui.notify('error', 'Could not save driver', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const create = async (body: Record<string, unknown>) => {
    try {
      const created = (await api.createTechnician(territoryId, body)) as { id?: string };
      setNewOpen(false);
      const list = await load();
      const target = list.find((d) => d.id === created.id) ?? list[0];
      if (target) select(target);
      ui.notify('ok', 'Driver added');
    } catch (err) {
      ui.notify(
        'error',
        'Could not add the driver',
        err instanceof ApiError ? err.message : String(err),
      );
    }
  };

  const remove = async () => {
    if (!draft) return;
    const ok = await ui.confirm({
      title: `Delete ${draft.name}?`,
      body: 'Any vehicle they drive becomes unassigned. Work they already completed keeps its history.',
      confirmLabel: 'Delete driver',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteTechnician(territoryId, draft.id);
      const list = await load();
      if (list[0]) select(list[0]);
      else {
        setSelected(null);
        setDraft(null);
      }
      ui.notify('ok', 'Driver deleted');
    } catch (err) {
      ui.notify('error', 'Could not delete', err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="master-detail">
      <div className="master">
        <div className="side-head">
          <h2>Drivers</h2>
          <span className="count-pill">{drivers.length}</span>
          <IconButton small label="Send a notification to drivers" onClick={() => setCompose({ preselect: null })}>
            <BellIcon />
          </IconButton>
          <IconButton small label="New driver" onClick={() => setNewOpen(true)}>
            <PlusIcon />
          </IconButton>
        </div>
        <div className="side-list">
          {drivers.map((d) => (
            <button
              key={d.id}
              type="button"
              className="order-row"
              data-selected={d.id === selected?.id ? 'true' : undefined}
              onClick={() => select(d)}
            >
              <b>{d.name}</b>
              {d.active ? null : (
                <span className="delta" data-tone="idle">
                  inactive
                </span>
              )}
              <small>{d.email ?? d.phone ?? '-'}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="detail">
        {draft ? (
          <SettingsCard
            title={draft.name}
            description="Identity, and what this technician sees in the app."
            editing={editing}
            onEdit={() => setEditing(true)}
            onCancel={() => {
              setEditing(false);
              if (selected) setDraft({ ...selected });
            }}
            onSave={() => void save()}
            saving={saving}
            actions={
              <IconButton tone="danger" label="Delete driver" onClick={() => void remove()}>
                <TrashIcon />
              </IconButton>
            }
          >
            <Field label="Name">
              <TextInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
            </Field>
            <Field label="Email">
              <TextInput
                value={draft.email ?? ''}
                onChange={(v) => setDraft({ ...draft, email: v || null })}
              />
            </Field>
            <Field label="Phone">
              <TextInput
                value={draft.phone ?? ''}
                onChange={(v) => setDraft({ ...draft, phone: v || null })}
              />
            </Field>
            <Field label="Tags" hint="Skills the planner matches against an order">
              <TagsInput
                value={draft.tags}
                placeholder="senior tech, sealed system"
                onChange={(tags) => setDraft({ ...draft, tags })}
              />
            </Field>
            <Field label="Active">
              <Toggle
                checked={draft.active}
                onChange={(v) => setDraft({ ...draft, active: v })}
                label={draft.active ? 'Working' : 'Inactive'}
              />
            </Field>

            <h3 className="section-label">Driver app</h3>
            <Field
              label="Days visible"
              hint="How far ahead they can see. Zero inherits the territory setting."
            >
              <NumberInput
                value={draft.app_visible_days ?? 0}
                min={0}
                max={14}
                suffix="days"
                onChange={(v) => setDraft({ ...draft, app_visible_days: v > 0 ? v : null })}
              />
            </Field>
            <Field label="Upcoming days shown as">
              <Select
                value={draft.app_upcoming_detail ?? 'inherit'}
                options={[
                  { value: 'inherit', label: 'Inherit territory' },
                  { value: 'full', label: 'Full details' },
                  { value: 'summary', label: 'Summary' },
                  { value: 'count', label: 'Count only' },
                ]}
                onChange={(v) =>
                  setDraft({ ...draft, app_upcoming_detail: v === 'inherit' ? null : v })
                }
              />
            </Field>
            <Field label="Add new barcodes">
              <Toggle
                checked={draft.app_allow_barcode_add === true}
                onChange={(v) => setDraft({ ...draft, app_allow_barcode_add: v })}
                label={draft.app_allow_barcode_add ? 'Allowed' : 'Disabled'}
              />
            </Field>
          </SettingsCard>
        ) : (
          <div className="empty">Select a driver.</div>
        )}
        {selected ? (
          <DriverSignIn
            territoryId={territoryId}
            driver={selected}
            onChanged={() =>
              void load().then((list) => {
                const fresh = list.find((d) => d.id === selected.id);
                if (fresh) {
                  setSelected(fresh);
                  if (!editing) setDraft({ ...fresh });
                }
              })
            }
          />
        ) : null}
        {selected ? (
          <DriverNotifyCard
            territoryId={territoryId}
            driver={selected}
            refreshKey={sentCount}
            onCompose={() => setCompose({ preselect: [selected.id] })}
          />
        ) : null}
      </div>

      <SendNotificationDrawer
        open={compose !== null}
        territoryId={territoryId}
        drivers={drivers}
        preselect={compose?.preselect ?? null}
        onClose={() => setCompose(null)}
        onSent={() => setSentCount((n) => n + 1)}
      />

      <NewDriverDrawer
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreate={(body) => void create(body)}
      />
    </div>
  );
}

function NewDriverDrawer({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tags, setTags] = useState('');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New driver"
      subtitle="Assign them a vehicle on Driver assignment once they exist"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim()}
            onClick={() =>
              onCreate({
                name: name.trim(),
                email: email.trim() || null,
                phone: phone.trim() || null,
                tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
                active: true,
              })
            }
          >
            Create driver
          </button>
        </>
      }
    >
      <Field label="Name">
        <TextInput value={name} onChange={setName} placeholder="Alex Rivera" />
      </Field>
      <Field label="Email">
        <TextInput value={email} onChange={setEmail} />
      </Field>
      <Field label="Phone">
        <TextInput value={phone} onChange={setPhone} />
      </Field>
      <Field label="Tags" hint="Comma separated">
        <TextInput value={tags} onChange={setTags} placeholder="senior tech, sealed system" />
      </Field>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ *
 * Territory
 * ------------------------------------------------------------------ */

interface Territory extends Record<string, unknown> {
  id: string;
  name: string;
  timezone: string;
}

/**
 * Territories.
 *
 * An operation can run several -- a metro each, or a region each -- and every
 * other settings page, the board, the orders and the plans are scoped to
 * whichever one is selected. So this page both lists them and switches between
 * them: picking one here is the same act as picking one in the header, and
 * having two controls that disagreed about which territory you were configuring
 * would be a genuinely dangerous kind of confusion.
 */
function TerritoryPage({
  territoryId,
  onSelectTerritory,
  onTerritoriesChanged,
}: {
  territoryId: string | null;
  onSelectTerritory: (territoryId: string) => void;
  onTerritoriesChanged: () => void;
}) {
  const ui = useUi();
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [draft, setDraft] = useState<Territory | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  /*
   * The callback lives in a ref, not in a dependency list.
   *
   * `load` used to depend on `onTerritoriesChanged`, which the shell passes as
   * an inline arrow -- a new function on every one of its renders. So `load`
   * got a new identity, the effect below saw its dependencies change, and it
   * ran again and called `setEditing(false)`. And `load` itself calls that
   * callback, which reloads the header, which re-renders the shell, which makes
   * another new arrow: a loop that any render could start.
   *
   * The visible symptom was the edit pencil appearing to do nothing. The form
   * did open; the next render of the shell closed it, and on a board patching
   * itself from the live feed the next render is never far away. The pencil
   * worked perfectly for as long as nothing else on screen moved, which is the
   * hardest kind of bug to catch by trying it.
   */
  const notifyChanged = useRef(onTerritoriesChanged);
  notifyChanged.current = onTerritoriesChanged;

  const load = useCallback(async () => {
    const { territories: list } = await api.territories();
    setTerritories(list as unknown as Territory[]);
    // The header shows the same list; keep it from going stale.
    notifyChanged.current();
    return list as unknown as Territory[];
  }, []);

  /* Only a change of territory resets the form. Reloading the list does not. */
  useEffect(() => {
    void load().then((list) => {
      const current = list.find((t) => t.id === territoryId) ?? list[0];
      if (current) setDraft({ ...current });
      setEditing(false);
    });
  }, [load, territoryId]);

  const select = (t: Territory) => {
    setDraft({ ...t });
    setEditing(false);
    // Everything else on the screen follows the selection.
    if (t.id !== territoryId) onSelectTerritory(t.id);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.updateTerritory(draft.id, {
        name: draft.name,
        timezone: draft.timezone,
        planningHorizonDays: draft.planning_horizon_days,
        borderRestriction: draft.border_restriction,
        orderQuota: draft.order_quota,
        routeBalance: draft.route_balance,
        centerLat: draft.center_lat ?? null,
        centerLng: draft.center_lng ?? null,
      });
      ui.notify('ok', 'Territory saved');
      const list = await load();
      const fresh = list.find((t) => t.id === draft.id);
      if (fresh) setDraft({ ...fresh });
      setEditing(false);
    } catch (err) {
      ui.notify('error', 'Could not save', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const create = async (body: Record<string, unknown>) => {
    try {
      const created = (await api.createTerritory(body)) as { id?: string };
      setNewOpen(false);
      const list = await load();
      const target = list.find((t) => t.id === created.id);
      if (target) select(target);
      ui.notify(
        'ok',
        'Territory created',
        'It has no depots, vehicles or drivers yet -- add those before planning a day.',
      );
    } catch (err) {
      ui.notify(
        'error',
        'Could not create the territory',
        err instanceof ApiError ? err.message : String(err),
      );
    }
  };

  const remove = async () => {
    if (!draft) return;
    const ok = await ui.confirm({
      title: `Delete ${draft.name}?`,
      body: 'Only an empty territory can be deleted. Anything it still holds has to be removed first.',
      confirmLabel: 'Delete territory',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteTerritory(draft.id);
      const list = await load();
      if (list[0]) select(list[0]);
      ui.notify('ok', 'Territory deleted');
    } catch (err) {
      /*
       * The refusal names what the territory still holds. Show it in full: the
       * message is the instruction for what to do next.
       */
      ui.notify('error', 'Not deleted', err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="master-detail">
      <div className="master">
        <div className="side-head">
          <h2>Territories</h2>
          <span className="count-pill">{territories.length}</span>
          <IconButton small label="New territory" onClick={() => setNewOpen(true)}>
            <PlusIcon />
          </IconButton>
        </div>
        <div className="side-list">
          {territories.map((t) => (
            <button
              key={t.id}
              type="button"
              className="order-row"
              data-selected={t.id === draft?.id ? 'true' : undefined}
              onClick={() => select(t)}
            >
              <b>{t.name}</b>
              {t.id === territoryId ? (
                <span className="delta" data-tone="ok">
                  active
                </span>
              ) : null}
              <small>{String(t.timezone)}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="detail">
        {draft ? (
          <SettingsCard
            title={draft.name}
            description="Essential settings for this territory."
            editing={editing}
            onEdit={() => setEditing(true)}
            onCancel={() => {
              setEditing(false);
              const original = territories.find((t) => t.id === draft.id);
              if (original) setDraft({ ...original });
            }}
            onSave={() => void save()}
            saving={saving}
            actions={
              <IconButton tone="danger" label="Delete territory" onClick={() => void remove()}>
                <TrashIcon />
              </IconButton>
            }
          >
            {/*
              * Storage leads their screen, as a bar and a fraction. We stored
              * the quota and never showed consumption, which is the half that
              * matters -- a bulk import that will not fit is worth knowing
              * before it is attempted.
              */}
            <Field label="Storage" wide>
              <Quota used={Number(draft.order_count ?? 0)} limit={Number(draft.order_quota ?? 1500)} />
            </Field>
            <Field label="Name">
              <TextInput value={draft.name} onChange={(x) => setDraft({ ...draft, name: x })} />
            </Field>
            <Field label="Timezone" hint="IANA name, e.g. America/New_York">
              <TextInput
                value={draft.timezone}
                onChange={(x) => setDraft({ ...draft, timezone: x })}
              />
            </Field>
            <Field label="Planning range" hint="How many days ahead may be planned">
              <NumberInput
                value={Number(draft.planning_horizon_days ?? 7)}
                min={1}
                max={31}
                suffix="days"
                onChange={(x) => setDraft({ ...draft, planning_horizon_days: x })}
              />
            </Field>
            <Field label="Border restriction">
              <Select
                value={String(draft.border_restriction ?? 'none')}
                options={[
                  { value: 'none', label: 'No restrictions' },
                  { value: 'territory', label: 'Stay inside the territory' },
                  { value: 'country', label: 'Do not cross a country border' },
                ]}
                onChange={(x) => setDraft({ ...draft, border_restriction: x })}
              />
            </Field>
            {/*
              * Ours, not the incumbent's: how the route builder shares the day
              * between vehicles. Without it an unpriced fleet got every job on
              * one van, which is cheapest on paper and not a plan anyone sends.
              */}
            <Field label="Route balance" hint="How Build Routes shares work between vehicles">
              <Select
                value={String(draft.route_balance ?? 'balanced')}
                options={[
                  { value: 'balanced', label: 'Balanced', hint: 'similar day for every van' },
                  { value: 'efficient', label: 'Most efficient', hint: 'least total time' },
                  { value: 'earliest', label: 'Finish earliest', hint: 'shortest longest day' },
                ]}
                onChange={(x) => setDraft({ ...draft, route_balance: x })}
              />
            </Field>
            <Field label="Order quota">
              <NumberInput
                value={Number(draft.order_quota ?? 1500)}
                min={1}
                suffix="orders"
                onChange={(x) => setDraft({ ...draft, order_quota: x })}
              />
            </Field>
            {/*
              * Not on the incumbent's territory screen, which carries four
              * fields and no centre -- theirs is chosen when a territory is
              * created and only ever read back through the API afterwards.
              * Added at the operator's request, because a value their API
              * reports and nothing here can set is a value stuck at null.
              * Degrees; the compat surface converts to their microdegree pair.
              */}
            <Field label="Centre latitude" hint="Where a map opens on this territory">
              <NumberInput
                value={Number(draft.center_lat ?? 0)}
                min={-90}
                max={90}
                step={0.000001}
                onChange={(x) => setDraft({ ...draft, center_lat: x })}
              />
            </Field>
            <Field label="Centre longitude">
              <NumberInput
                value={Number(draft.center_lng ?? 0)}
                min={-180}
                max={180}
                step={0.000001}
                onChange={(x) => setDraft({ ...draft, center_lng: x })}
              />
            </Field>
          </SettingsCard>
        ) : (
          territories.length === 0 ? (
            <div className="setup-empty">
              <h2>No territories yet</h2>
              <p>A territory is an area you dispatch in, with its own depots, vehicles, drivers and timezone.</p>
              <button type="button" className="btn btn-primary" onClick={() => setNewOpen(true)}>
                Create your first territory
              </button>
            </div>
          ) : (
            <div className="empty">Select a territory.</div>
          )
        )}
      </div>

      <NewTerritoryDrawer
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreate={(body) => void create(body)}
      />
    </div>
  );
}

/** A territory starts empty; everything else about it is set afterwards. */
function NewTerritoryDrawer({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(browserTimezone);
  const [horizon, setHorizon] = useState(7);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New territory"
      subtitle="Depots, vehicles and drivers are added to it afterwards"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim() || !timezone.trim()}
            onClick={() =>
              onCreate({
                name: name.trim(),
                timezone: timezone.trim(),
                planningHorizonDays: horizon,
              })
            }
          >
            Create territory
          </button>
        </>
      }
    >
      <Field label="Name">
        <TextInput value={name} onChange={setName} placeholder="Columbus" />
      </Field>
      <Field label="Timezone" hint="Every clock time in this territory is local to it.">
        <TimezoneSelect value={timezone} onChange={setTimezone} />
      </Field>
      <Field label="Planning range">
        <NumberInput value={horizon} min={1} max={31} suffix="days" onChange={setHorizon} />
      </Field>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ *
 * Tracking page (captured from the incumbent)
 * ------------------------------------------------------------------ */

function TrackingPagePage({ territoryId }: { territoryId: string }) {
  const record = useRecord<Record<string, unknown>>(
    () => api.communication(territoryId),
    (patch) =>
      api.saveCommunication(territoryId, {
        companyName: patch.company_name,
        tagline: patch.tagline,
        phone: patch.phone,
        phoneVisible: patch.phone_visible,
        email: patch.email,
        emailVisible: patch.email_visible,
        emailReplyTo: patch.email_reply_to,
        showEtaRange: patch.show_eta_range,
        etaRangeMinutes: patch.eta_range_minutes,
        podPictures: patch.pod_pictures,
        podSignature: patch.pod_signature,
        podVoiceMemos: patch.pod_voice_memos,
        podDriverNotes: patch.pod_driver_notes,
      }),
    'Tracking page',
  );

  const v = record.value;
  if (!v) return <div className="empty">Loading.</div>;

  return (
    <div className="view">
      <SettingsCard
        title="Tracking page"
        description="What customers see on the tracking link."
        onSave={() => void record.commit()}
        saving={record.saving}
        dirty={record.dirty}
      >
        <Field label="Company name">
          <TextInput
            value={String(v.company_name ?? '')}
            onChange={(x) => record.set('company_name', x)}
          />
        </Field>
        <Field label="Tagline">
          <TextInput value={String(v.tagline ?? '')} onChange={(x) => record.set('tagline', x)} />
        </Field>
        <Field label="Phone number">
          <TextInput value={String(v.phone ?? '')} onChange={(x) => record.set('phone', x)} />
        </Field>
        <Field label="Show phone">
          <Toggle
            checked={Boolean(v.phone_visible)}
            onChange={(x) => record.set('phone_visible', x)}
            label={v.phone_visible ? 'Visible' : 'Hidden'}
          />
        </Field>
        <Field label="Email address">
          <TextInput value={String(v.email ?? '')} onChange={(x) => record.set('email', x)} />
        </Field>
        <Field label="Show email">
          <Toggle
            checked={Boolean(v.email_visible)}
            onChange={(x) => record.set('email_visible', x)}
            label={v.email_visible ? 'Visible' : 'Hidden'}
          />
        </Field>
        <Field label="Use as reply-to">
          <Toggle
            checked={Boolean(v.email_reply_to)}
            onChange={(x) => record.set('email_reply_to', x)}
          />
        </Field>

        <h3 className="section-label">Arrival time</h3>
        <Field
          label="Show an ETA range"
          hint="A single time is false precision; uncertainty grows down the route"
        >
          <Toggle
            checked={Boolean(v.show_eta_range)}
            onChange={(x) => record.set('show_eta_range', x)}
            label={v.show_eta_range ? 'Range' : 'Exact time'}
          />
        </Field>
        <Field label="Range width">
          <NumberInput
            value={Number(v.eta_range_minutes ?? 60)}
            min={5}
            max={480}
            suffix="min"
            onChange={(x) => record.set('eta_range_minutes', x)}
          />
        </Field>

        <h3 className="section-label">Proof of delivery</h3>
        <Field label="Pictures">
          <Toggle checked={Boolean(v.pod_pictures)} onChange={(x) => record.set('pod_pictures', x)} />
        </Field>
        <Field label="Signature">
          <Toggle
            checked={Boolean(v.pod_signature)}
            onChange={(x) => record.set('pod_signature', x)}
          />
        </Field>
        <Field label="Voice memos">
          <Toggle
            checked={Boolean(v.pod_voice_memos)}
            onChange={(x) => record.set('pod_voice_memos', x)}
          />
        </Field>
        <Field label="Driver notes">
          <Toggle
            checked={Boolean(v.pod_driver_notes)}
            onChange={(x) => record.set('pod_driver_notes', x)}
          />
        </Field>
      </SettingsCard>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Driver mobile app
 * ------------------------------------------------------------------ */

function MobileAppPage({ territoryId }: { territoryId: string }) {
  const record = useRecord<Record<string, unknown>>(
    () => api.mobileApp(territoryId),
    (patch) =>
      api.saveMobileApp(territoryId, {
        visibleDays: patch.visible_days,
        upcomingDetail: patch.upcoming_detail,
        allowBarcodeAdd: patch.allow_barcode_add,
        allowReorder: patch.allow_reorder,
        requireSignature: patch.require_signature,
        requirePhoto: patch.require_photo,
        showCustomerPhone: patch.show_customer_phone,
        pingInterval: patch.ping_interval,
      }),
    'App settings',
  );

  const v = record.value;
  if (!v) return <div className="empty">Loading.</div>;

  return (
    <div className="view">
      <NotCaptured what="The territory-level driver app configuration" />
      <SettingsCard
        title="Driver mobile app"
        description="Defaults for every technician. Individual drivers can override the viewing window."
        onSave={() => void record.commit()}
        saving={record.saving}
        dirty={record.dirty}
      >
        <Field label="Days visible" hint="How far ahead a technician can see">
          <NumberInput
            value={Number(v.visible_days ?? 3)}
            min={1}
            max={14}
            suffix="days"
            onChange={(x) => record.set('visible_days', x)}
          />
        </Field>
        <Field label="Upcoming days shown as">
          <Select
            value={String(v.upcoming_detail ?? 'full')}
            options={[
              { value: 'full', label: 'Full details' },
              { value: 'summary', label: 'Summary' },
              { value: 'count', label: 'Count only' },
            ]}
            onChange={(x) => record.set('upcoming_detail', x)}
          />
        </Field>
        <Field label="Add new barcodes">
          <Toggle
            checked={Boolean(v.allow_barcode_add)}
            onChange={(x) => record.set('allow_barcode_add', x)}
          />
        </Field>
        <Field label="Reorder own stops">
          <Toggle
            checked={Boolean(v.allow_reorder)}
            onChange={(x) => record.set('allow_reorder', x)}
          />
        </Field>
        <Field label="Signature required">
          <Toggle
            checked={Boolean(v.require_signature)}
            onChange={(x) => record.set('require_signature', x)}
          />
        </Field>
        <Field label="Photo required">
          <Toggle
            checked={Boolean(v.require_photo)}
            onChange={(x) => record.set('require_photo', x)}
          />
        </Field>
        <Field label="Show customer phone">
          <Toggle
            checked={Boolean(v.show_customer_phone)}
            onChange={(x) => record.set('show_customer_phone', x)}
          />
        </Field>
        <Field label="Position reporting" hint="Lower drains the battery faster">
          <NumberInput
            value={Number(v.ping_interval ?? 60)}
            min={15}
            max={900}
            suffix="sec"
            onChange={(x) => record.set('ping_interval', x)}
          />
        </Field>
      </SettingsCard>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Regions, road editor, traffic, notifications
 * ------------------------------------------------------------------ */

function RegionsPage({ territoryId }: { territoryId: string }) {
  const ui = useUi();
  const [regions, setRegions] = useState<Record<string, unknown>[]>([]);

  const load = useCallback(async () => {
    const { regions: list } = await api.regions(territoryId);
    setRegions(list);
  }, [territoryId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: string, name: string) => {
    const ok = await ui.confirm({
      title: `Delete ${name}?`,
      body: 'Any vehicle restricted to or from this region loses that constraint.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await api.deleteRegion(territoryId, id);
    await load();
  };

  return (
    <div className="view">
      <NotCaptured what="Regions" />
      <SettingsCard
        title="Regions"
        description="Areas a vehicle must stay inside, must avoid, or that cost extra to enter. The planner honours all three. Regions are drawn through the API."
      >
        {regions.length === 0 ? (
          <EmptyState
            title="No Regions"
            body="Regions are areas a vehicle must stay inside, must avoid, or that cost extra to enter. Draw them through the API and they appear here."
          />
        ) : (
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Kind</th>
                  <th>Entry cost</th>
                  <th>Entry time</th>
                  <th>Vehicles</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {regions.map((r) => (
                  <tr key={String(r.id)}>
                    <td>
                      <b>{String(r.name)}</b>
                    </td>
                    <td>{String(r.kind)}</td>
                    <td className="num">{String(r.entry_cost)}</td>
                    <td className="num">{Math.round(Number(r.entry_time) / 60)}m</td>
                    <td className="num">{String(r.vehicle_count ?? 0)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void remove(String(r.id), String(r.name))}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}

function RoadEditorPage({ territoryId }: { territoryId: string }) {
  const [edits, setEdits] = useState<Record<string, unknown>[]>([]);

  useEffect(() => {
    void api.roadEdits(territoryId).then(({ edits: list }) => setEdits(list));
  }, [territoryId]);

  return (
    <div className="view">
      <NotCaptured what="The road editor" />
      <SettingsCard
        title="Road edits"
        description="Local corrections to the road network: a closed street, a wrong speed, an illegal turn."
      >
        <div className="banner" data-tone="warn">
          <div>
            <b>Edits are stored but not yet enforced</b>
            <p>
              Routing is decided by the matrix provider, so an edit only takes effect once it has
              been compiled into OSRM or Valhalla. Against the haversine fallback it does nothing
              at all.
            </p>
          </div>
        </div>
        {edits.length === 0 ? (
          <EmptyState
            title="No Road Segments"
            body="A road segment closes a road, changes its speed, or forbids a turn. Nothing is edited until one exists."
          />
        ) : (
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Speed</th>
                  <th>Active</th>
                  <th>In engine</th>
                </tr>
              </thead>
              <tbody>
                {edits.map((e) => (
                  <tr key={String(e.id)}>
                    <td>
                      <b>{String(e.name)}</b>
                    </td>
                    <td>{String(e.kind)}</td>
                    <td className="num">{e.speed_kmh ? `${e.speed_kmh} km/h` : '-'}</td>
                    <td>{e.active ? 'yes' : 'no'}</td>
                    <td>{e.applied ? 'yes' : 'no'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}

function TrafficPage({ territoryId }: { territoryId: string }) {
  const ui = useUi();
  const [factors, setFactors] = useState<number[]>(() => new Array(24).fill(1));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.traffic(territoryId).then(({ entries }) => {
      const next = new Array(24).fill(1);
      for (const e of entries) {
        if (e.weekday === null) next[e.hour] = Number(e.factor);
      }
      setFactors(next);
      setDirty(false);
    });
  }, [territoryId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveTraffic(
        territoryId,
        factors.map((factor, hour) => ({ weekday: null, hour, factor })),
      );
      ui.notify('ok', 'Traffic profile saved');
      setDirty(false);
    } catch (err) {
      ui.notify('error', 'Could not save', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="view">
      <NotCaptured what="Traffic configuration" />
      <SettingsCard
        title="Traffic profile"
        description="How much longer legs take at each hour. 1.0 is free flow. These will eventually be derived from your own GPS history rather than typed."
        onSave={() => void save()}
        saving={saving}
        dirty={dirty}
      >
        <div className="traffic-grid">
          {factors.map((f, hour) => (
            <label key={hour} className="traffic-cell">
              <span className="mono">{String(hour).padStart(2, '0')}:00</span>
              <input
                type="number"
                className="field"
                min={0.5}
                max={4}
                step={0.05}
                value={f}
                onChange={(e) => {
                  const next = [...factors];
                  next[hour] = Number(e.target.value);
                  setFactors(next);
                  setDirty(true);
                }}
              />
            </label>
          ))}
        </div>
      </SettingsCard>
    </div>
  );
}

/**
 * Communication preferences: the defaults every customer message is written
 * with.
 *
 * Their four fields exactly -- language, sender name, and the time and date
 * formats a message renders in. These are the message's formats, not the
 * console's: a customer in another locale reads what is written here, not what
 * the dispatcher's browser happens to prefer.
 */
function PreferencesPage({ territoryId }: { territoryId: string }) {
  const record = useRecord<Record<string, unknown>>(
    () => api.communication(territoryId),
    (patch) =>
      api.saveCommunication(territoryId, {
        language: patch.language,
        senderName: patch.sender_name,
        timeFormat: patch.time_format,
        dateFormat: patch.date_format,
      }),
    'Preferences',
  );

  const v = record.value;
  if (!v) return <div className="empty">Loading.</div>;

  return (
    <div className="view">
      <SettingsCard
        title="Preferences"
        description="Defaults for customer notifications."
        onSave={() => void record.commit()}
        saving={record.saving}
        dirty={record.dirty}
      >
        <Field label="Communication language">
          <Select
            value={String(v.language ?? 'en')}
            options={[
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Spanish' },
              { value: 'fr', label: 'French' },
              { value: 'de', label: 'German' },
            ]}
            onChange={(x) => record.set('language', x)}
          />
        </Field>
        <Field label="Sender name" hint="Who the message appears to come from">
          <TextInput
            value={String(v.sender_name ?? '')}
            placeholder="Smart Appliance Repair"
            onChange={(x) => record.set('sender_name', x)}
          />
        </Field>
        <Field label="Time format">
          <Select
            value={String(v.time_format ?? 'h12')}
            options={[
              { value: 'h12', label: '2:00 PM' },
              { value: 'h24', label: '14:00' },
            ]}
            onChange={(x) => record.set('time_format', x)}
          />
        </Field>
        <Field label="Date format">
          <Select
            value={String(v.date_format ?? 'MMM d')}
            options={[
              { value: 'MMM d', label: 'Feb 15' },
              { value: 'd MMM', label: '15 Feb' },
              { value: 'MM/dd', label: '02/15' },
              { value: 'dd/MM', label: '15/02' },
            ]}
            onChange={(x) => record.set('date_format', x)}
          />
        </Field>
      </SettingsCard>
    </div>
  );
}

/**
 * Voice: masked calling between a technician and a customer.
 *
 * Theirs is one status field, and that is genuinely all it is -- not automated
 * voice notifications, which is what we had assumed and described. A driver
 * rings the customer through a number that hides both real ones.
 */
function VoicePage({ territoryId }: { territoryId: string }) {
  const record = useRecord<Record<string, unknown>>(
    () => api.communication(territoryId),
    (patch) => api.saveCommunication(territoryId, { voiceEnabled: patch.voice_enabled }),
    'Voice',
  );

  const v = record.value;
  if (!v) return <div className="empty">Loading.</div>;

  return (
    <div className="view">
      <SettingsCard
        title="Voice"
        description="Allow calls between drivers and customers before or after a job."
        onSave={() => void record.commit()}
        saving={record.saving}
        dirty={record.dirty}
      >
        <div className="banner" data-tone="warn">
          <div>
            <b>Stored, not connected</b>
            <p>
              Masked calling needs a telephony provider, and none is configured. Switching this on
              records the intent and places no calls.
            </p>
          </div>
        </div>
        <Field label="Status">
          <Toggle
            checked={Boolean(v.voice_enabled)}
            onChange={(x) => record.set('voice_enabled', x)}
            label={v.voice_enabled ? 'On' : 'Off'}
          />
        </Field>
      </SettingsCard>
    </div>
  );
}

/** The quota bar: how much of this territory's order allowance is used. */
function Quota({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  /* Amber near the ceiling, red at it -- the same three-tone rule as the board. */
  const tone = pct >= 100 ? 'late' : pct >= 85 ? 'warn' : undefined;
  return (
    <div className="quota">
      <div className="quota-bar">
        <div className="quota-fill" data-tone={tone} style={{ width: `${pct}%` }} />
      </div>
      <span className="quota-note">
        {used.toLocaleString()} of {limit.toLocaleString()} orders · {pct}%
      </span>
    </div>
  );
}

/**
 * The empty state the incumbent uses on every unpopulated screen: a drawing, a
 * heading, one line, and a way forward.
 *
 * Ours was a line of grey text, which reads as a screen that has not been built
 * rather than one with nothing in it yet -- a real difference when half of
 * these screens genuinely are unbuilt elsewhere in the product.
 */
function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty-state">
      <EmptyIllustration />
      <b>{title}</b>
      <p>{body}</p>
      {action}
    </div>
  );
}

function EmptyIllustration() {
  return (
    <svg width="96" height="72" viewBox="0 0 96 72" fill="none" aria-hidden="true">
      <ellipse cx="48" cy="38" rx="40" ry="30" fill="var(--surface-3)" />
      <rect x="22" y="18" width="40" height="10" rx="3" fill="var(--surface)"
        stroke="var(--line-strong)" strokeWidth="1.3" />
      <rect x="30" y="33" width="44" height="10" rx="3" fill="var(--accent-dim)"
        stroke="var(--accent)" strokeWidth="1.3" />
      <rect x="26" y="48" width="34" height="10" rx="3" fill="var(--surface)"
        stroke="var(--line-strong)" strokeWidth="1.3" />
    </svg>
  );
}
